import { SortableContext } from '@dnd-kit/sortable'
import { SkeletonWrapper, Text } from '@shared/components'

import { getContrastTextColor } from '@shared/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button, Card, Flex, Tag } from 'antd'
import { PresetStatusColorType } from 'antd/es/_util/colors'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { KanbanDroppable } from './KanbanDroppableContent'
import { KanbanSortable } from './KanbanSortable'
import { useStyles } from './styles'
import { KANBAN_PER_PAGE } from '../Kanban/utils'

import type {
  CardWithId,
  InfiniteQueryOptionsFn,
  PaginatedResponse,
  PipelineQueryOptions,
  QueryKeyFn,
  StatusWithRefusalReason
} from '@shared/types'

type Props<
  TCard extends CardWithId,
  TStatus extends StatusWithRefusalReason,
  TPipelineQueryOptions extends PipelineQueryOptions
> = {
  columnData: TStatus
  cardRender: (data: TCard) => React.ReactNode
  pipelineListQuery: InfiniteQueryOptionsFn<TPipelineQueryOptions>
  pipelineListQueryKey: QueryKeyFn<TPipelineQueryOptions>
  pipelineListQueryOptions: TPipelineQueryOptions
  isDisabled?: boolean
}

const KanbanColumnComponent = <
  TCard extends CardWithId,
  TStatus extends StatusWithRefusalReason,
  TPipelineQueryOptions extends PipelineQueryOptions
>({
  columnData,
  cardRender,
  pipelineListQuery,
  pipelineListQueryKey,
  pipelineListQueryOptions,
  isDisabled,
  ...props
}: Props<TCard, TStatus, TPipelineQueryOptions>) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const query = {
    ...pipelineListQueryOptions,
    per_page: KANBAN_PER_PAGE,
    status: columnData?.id
  }
  const { data, fetchNextPage, isLoading, hasNextPage, isFetching } =
    useInfiniteQuery({
      ...(pipelineListQuery({ query }) as ReturnType<typeof useInfiniteQuery>),
      queryKey: pipelineListQueryKey({ query }),
      getNextPageParam: (lastPage) => {
        const page = lastPage as PaginatedResponse<TCard>

        return page?.next
      },
      initialPageParam: 1
    })

  const columnDataItems =
    data?.pages?.flatMap(
      (page) => (page as PaginatedResponse<TCard>)?.results ?? []
    ) ?? []

  return (
    <SortableContext
      id={String(columnData?.id)}
      items={columnDataItems?.map((data) => String(data?.id))}
      {...props}
    >
      <Flex vertical gap={10} justify="space-between">
        <Card
          variant="outlined"
          className={`${styles.card}${
            isDisabled ? ` ${styles.disabledCard}` : ''
          }`}
        >
          <SkeletonWrapper active paragraph={{ rows: 20 }} loading={isLoading}>
            <KanbanDroppable id={String(columnData?.id)}>
              <Flex align="center" justify="space-between">
                <Flex align="start" gap={10}>
                  <Text weight={600}>{t(columnData?.name ?? '')}</Text>

                  <Tag color={columnData?.color as PresetStatusColorType}>
                    <Text
                      weight={600}
                      size={12}
                      style={{ color: getContrastTextColor(columnData?.color) }}
                    >
                      {(
                        data?.pages?.[0] as PaginatedResponse<TCard> | undefined
                      )?.count ?? 0}
                    </Text>
                  </Tag>
                </Flex>
              </Flex>

              <Flex
                vertical
                justify="space-between"
                gap={16}
                className={styles.column}
              >
                <Flex vertical gap={16}>
                  {columnDataItems?.map((data) => (
                    <KanbanSortable
                      key={String(data?.id)}
                      id={String(data?.id)}
                    >
                      {cardRender?.(data)}
                    </KanbanSortable>
                  ))}
                </Flex>

                {hasNextPage && (
                  <Button
                    color="primary"
                    size="small"
                    loading={isFetching}
                    onClick={() => fetchNextPage()}
                  >
                    {t('load_more')}
                  </Button>
                )}
              </Flex>
            </KanbanDroppable>
          </SkeletonWrapper>
        </Card>
      </Flex>
    </SortableContext>
  )
}

export const KanbanColumn = memo(
  KanbanColumnComponent
) as typeof KanbanColumnComponent
